<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import catalogApi from '@/api/catalog'
import { useCatalogStore } from '@/store/catalog'
import { entityName, partitionAssignments } from '../shared/catalog.mjs'
import { normalizeCatalogError } from '../shared/catalogErrors.mjs'
import CatalogStatus from '../shared/CatalogStatus.vue'
import ConfirmDialog from '../shared/ConfirmDialog.vue'
import SearchableSelect from '@/components/forms/SearchableSelect.vue'

const props = defineProps({ categoryId: { type: String, required: true } })
const store = useCatalogStore()
const form = reactive({ characteristicId: '', sortOrder: 100 })
const error = ref(''); const success = ref(''); const saving = ref(false); const deleting = ref(null)
const partitioned = computed(() => partitionAssignments(store.effectiveAssignments, props.categoryId))
const direct = computed(() => store.directAssignments)
const inherited = computed(() => partitioned.value.inherited)
const categoryNames = computed(() => new Map(store.categories.items.map((item) => [String(item.id), entityName(item)])))
const availableOptions = computed(() => {
  const directIds = new Set(direct.value.map((item) => String(item.characteristicId)))
  return store.characteristicOptions.filter((item) => !directIds.has(String(item.id)))
})
function sourceName(item) { return categoryNames.value.get(String(item.sourceCategoryId)) || item.sourceCategoryId }
async function load() {
  if (!props.categoryId) return
  error.value = ''
  try {
    await Promise.all([
      store.loadCategoryCharacteristics(props.categoryId),
      store.loadCharacteristics({ page: 1, limit: 1000 }),
      store.loadCategories({ page: 1, limit: 1000 }),
    ])
  } catch (cause) { error.value = normalizeCatalogError(cause).message }
}
async function assign() {
  if (!form.characteristicId || saving.value) return
  saving.value = true; error.value = ''; success.value = ''
  try { await catalogApi.assignCharacteristic(props.categoryId, form.characteristicId, Number(form.sortOrder)); form.characteristicId = ''; form.sortOrder = 100; await store.loadCategoryCharacteristics(props.categoryId); success.value = 'Призначення збережено.' }
  catch (cause) { error.value = normalizeCatalogError(cause).message }
  finally { saving.value = false }
}
async function updateDirect(item) {
  if (saving.value) return
  saving.value = true; error.value = ''; success.value = ''
  try { await catalogApi.assignCharacteristic(props.categoryId, item.characteristicId, Number(item.sortOrder)); await store.loadCategoryCharacteristics(props.categoryId); success.value = 'Порядок прямої характеристики оновлено.' }
  catch (cause) { error.value = normalizeCatalogError(cause).message }
  finally { saving.value = false }
}
async function remove() {
  saving.value = true; error.value = ''; success.value = ''
  try { await catalogApi.deleteAssignment(props.categoryId, deleting.value.characteristicId); deleting.value = null; await store.loadCategoryCharacteristics(props.categoryId); success.value = 'Пряме призначення видалено.' }
  catch (cause) { error.value = normalizeCatalogError(cause).message }
  finally { saving.value = false }
}
onMounted(load); watch(() => props.categoryId, load)
</script>

<template>
  <CatalogStatus :loading="store.assignmentsLoading" :error="error" :success="success" @retry="load" />
  <form class="card bg-light border-0 mb-4" @submit.prevent="assign"><div class="card-body"><h3 class="h6">Додати пряму характеристику</h3><div class="row g-3 align-items-end">
    <div class="col-lg-7"><label class="form-label">Характеристика</label><SearchableSelect v-model="form.characteristicId" :options="availableOptions" select-placeholder="-- Оберіть характеристику --" search-placeholder="Пошук за code або назвою" no-results-text="Вільних характеристик немає" :disabled="saving" /></div>
    <div class="col-lg-3"><label class="form-label" for="assignment-sort">Sort order</label><input id="assignment-sort" v-model.number="form.sortOrder" type="number" step="1" class="form-control" :disabled="saving"></div>
    <div class="col-lg-2"><button class="btn btn-primary w-100" :disabled="saving || !form.characteristicId">Призначити</button></div>
  </div></div></form>

  <h3 class="h5">Прямі характеристики</h3>
  <div class="table-responsive mb-4"><table class="table table-sm align-middle"><thead><tr><th>Code</th><th>Назва</th><th>Sort</th><th>Джерело</th><th></th></tr></thead><tbody>
    <tr v-for="item in direct" :key="item.characteristicId"><td><code>{{ item.code }}</code></td><td>{{ entityName(item) }}</td><td><div class="input-group input-group-sm" style="min-width: 130px"><input v-model.number="item.sortOrder" type="number" step="1" class="form-control" :aria-label="`Порядок ${item.code}`" :disabled="saving"><button type="button" class="btn btn-outline-primary" :disabled="saving" aria-label="Зберегти порядок" @click="updateDirect(item)"><i class="bi bi-check-lg"></i></button></div></td><td><span class="badge bg-primary">Direct</span> {{ sourceName(item) }}</td><td class="text-end"><button type="button" class="btn btn-sm btn-outline-danger" :disabled="saving" @click="deleting = item"><i class="bi bi-trash"></i></button></td></tr>
    <tr v-if="!direct.length"><td colspan="5" class="text-center text-muted py-3">Прямих характеристик немає</td></tr>
  </tbody></table></div>

  <h3 class="h5">Успадковані характеристики</h3>
  <div class="table-responsive"><table class="table table-sm align-middle"><thead><tr><th>Code</th><th>Назва</th><th>Sort</th><th>Джерело</th></tr></thead><tbody>
    <tr v-for="item in inherited" :key="item.characteristicId"><td><code>{{ item.code }}</code></td><td>{{ entityName(item) }}</td><td>{{ item.sortOrder }}</td><td><span class="badge bg-secondary">Inherited</span> {{ sourceName(item) }}</td></tr>
    <tr v-if="!inherited.length"><td colspan="4" class="text-center text-muted py-3">Успадкованих характеристик немає</td></tr>
  </tbody></table></div>
  <ConfirmDialog :show="!!deleting" title="Видалити пряме призначення?" :busy="saving" @cancel="deleting = null" @confirm="remove">Якщо значення товарів втратять допустиму характеристику, backend поверне конфлікт і рядок залишиться на місці.</ConfirmDialog>
</template>
